import { useEffect, useMemo, useState } from 'react';
import api from '../api/client';
import { format } from 'date-fns';

function generarNumeros() {
  return Math.floor(100 + Math.random() * 900).toString();
}

function normalizarNombre(nombre) {
  return nombre
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, '');
}

function generarCredenciales(nombre) {
  const limpio = normalizarNombre(nombre);
  if (limpio.length < 3) return { username: '', password: '' };
  const prefijo = limpio.substring(0, 3);
  const numeros = generarNumeros();
  const base = `${prefijo}${numeros}`;
  return { username: base, password: base };
}

export default function Fincas() {
  const [fincas, setFincas] = useState([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [mostrarAdminModal, setMostrarAdminModal] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [form, setForm] = useState({ nombre: '', ubicacion: '' });
  const [adminForm, setAdminForm] = useState({
    nombre: '', email: '', username: '', password: '',
  });
  const [generado, setGenerado] = useState({ username: '', password: '' });

  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todas');

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    try {
      const { data } = await api.get('/fincas');
      setFincas(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fincasFiltradas = useMemo(() => {
    return fincas.filter((f) => {
      if (filtroEstado === 'activas' && !f.activo) return false;
      if (filtroEstado === 'inactivas' && f.activo) return false;
      if (busqueda.trim()) {
        const q = busqueda.trim().toLowerCase();
        const coincide =
          (f.nombre || '').toLowerCase().includes(q) ||
          (f.ubicacion || '').toLowerCase().includes(q);
        if (!coincide) return false;
      }
      return true;
    });
  }, [fincas, busqueda, filtroEstado]);

  const totalActivas = fincas.filter((f) => f.activo).length;
  const totalInactivas = fincas.filter((f) => !f.activo).length;

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroEstado('todas');
  };

  const hayFiltros = busqueda || filtroEstado !== 'todas';

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ nombre: '', ubicacion: '' });
    setMostrarModal(true);
  };

  const abrirEditar = (f) => {
    setEditando(f);
    setForm({ nombre: f.nombre, ubicacion: f.ubicacion || '' });
    setMostrarModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setMensaje(null);
    try {
      if (editando) {
        await api.put(`/fincas/${editando.id}`, form);
      } else {
        await api.post('/fincas', form);
      }
      setMostrarModal(false);
      await cargar();
      setMensaje({ tipo: 'ok', texto: 'Guardado correctamente' });
      setTimeout(() => setMensaje(null), 2500);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al guardar' });
    }
  };

  const desactivar = async (f) => {
    const ok = confirm(
      `⚠️ ¿Desactivar la finca "${f.nombre}"?\n\n` +
      `Consecuencias:\n` +
      `• Los usuarios de esta finca PERDERÁN ACCESO al sistema.\n` +
      `• Los datos (sectores, piscinas, registros, etc.) SE CONSERVAN.\n` +
      `• Podrás reactivarla después con el botón "🔓 Reactivar".\n\n` +
      `¿Confirmar desactivación?`
    );
    if (!ok) return;

    try {
      await api.delete(`/fincas/${f.id}`);
      await cargar();
      setMensaje({ tipo: 'ok', texto: `Finca "${f.nombre}" desactivada.` });
      setTimeout(() => setMensaje(null), 3000);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al desactivar' });
    }
  };

  const reactivar = async (f) => {
    const ok = confirm(`¿Reactivar la finca "${f.nombre}"?`);
    if (!ok) return;
    try {
      await api.put(`/fincas/${f.id}`, { activo: true });
      await cargar();
      setMensaje({ tipo: 'ok', texto: `Finca "${f.nombre}" reactivada.` });
      setTimeout(() => setMensaje(null), 2500);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al reactivar' });
    }
  };

  // ✅ ELIMINAR PERMANENTE (doble confirmación)
  const eliminarPermanente = async (f) => {
    const ok = confirm(
      `⚠️⚠️ ADVERTENCIA CRÍTICA ⚠️⚠️\n\n` +
      `Vas a ELIMINAR PERMANENTEMENTE la finca "${f.nombre}".\n\n` +
      `Esto eliminará TODOS los datos asociados:\n` +
      `• Todos los usuarios de la finca\n` +
      `• Todos los sectores y piscinas\n` +
      `• Todas las siembras, muestreos, cosechas\n` +
      `• Todo el registro diario e histórico\n` +
      `• Todas las actividades de alimentación\n` +
      `• Roles y permisos\n\n` +
      `⚠️ Esta acción NO se puede deshacer.\n\n` +
      `¿Estás completamente seguro?`
    );
    if (!ok) return;

    const confirmacion = prompt(
      `Para confirmar la eliminación permanente, escribe el nombre exacto de la finca:\n\n"${f.nombre}"`
    );
    if (confirmacion !== f.nombre) {
      alert('❌ Eliminación cancelada. El nombre no coincide.');
      return;
    }

    try {
      const { data } = await api.delete(`/fincas/${f.id}/permanente`);
      await cargar();
      setMensaje({
        tipo: 'ok',
        texto: data.mensaje || `Finca "${f.nombre}" eliminada permanentemente.`,
      });
      setTimeout(() => setMensaje(null), 5000);
    } catch (err) {
      setMensaje({
        tipo: 'error',
        texto: err.response?.data?.error || 'Error al eliminar permanentemente',
      });
    }
  };

  const abrirAdminModal = (f) => {
    setMostrarAdminModal(f);
    setAdminForm({ nombre: '', email: '', username: '', password: '' });
    setGenerado({ username: '', password: '' });
  };

  const handleAdminNombreChange = (nombre) => {
    setAdminForm(prev => ({ ...prev, nombre }));
    const cred = generarCredenciales(nombre);
    if (cred.username) {
      setGenerado(cred);
      setAdminForm(prev => ({
        ...prev, nombre,
        username: cred.username, password: cred.password,
      }));
    }
  };

  const regenerar = () => {
    const cred = generarCredenciales(adminForm.nombre);
    if (cred.username) {
      setGenerado(cred);
      setAdminForm(prev => ({ ...prev, username: cred.username, password: cred.password }));
    }
  };

  const guardarAdmin = async (e) => {
    e.preventDefault();
    setMensaje(null);
    try {
      await api.post(`/fincas/${mostrarAdminModal.id}/admin`, adminForm);
      setMostrarAdminModal(null);
      setMensaje({
        tipo: 'ok',
        texto: `✅ Admin creado para "${mostrarAdminModal.nombre}" con usuario ${adminForm.username}`,
      });
      setTimeout(() => setMensaje(null), 4000);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al crear admin' });
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-700">Fincas</h2>
        <button onClick={abrirNuevo} className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-800">
          + Nueva finca
        </button>
      </div>

      <div className="mb-4 p-3 bg-blue-50 text-blue-900 rounded text-sm">
        <strong>💡 Info:</strong> Cada finca tiene sus propios sectores, piscinas, usuarios y datos.
        Al <strong>desactivar</strong> una finca, sus usuarios pierden acceso pero los datos se conservan.
        Al <strong>eliminar permanentemente</strong>, se borra TODO de la base de datos (acción irreversible).
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">🔎 Buscar</label>
            <div className="relative">
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Nombre o ubicación…"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-sm"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="todas">Todas ({fincas.length})</option>
              <option value="activas">Activas ({totalActivas})</option>
              <option value="inactivas">Inactivas ({totalInactivas})</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
          <span>
            Mostrando <strong className="text-gray-700">{fincasFiltradas.length}</strong> de{' '}
            <strong className="text-gray-700">{fincas.length}</strong> fincas
          </span>
          {hayFiltros && (
            <button onClick={limpiarFiltros} className="text-blue-600 hover:underline">
              ✕ Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {mensaje && (
        <div className={`mb-4 p-3 rounded ${mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {mensaje.texto}
        </div>
      )}

      {fincas.length === 0 ? (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          No hay fincas. Crea la primera.
        </div>
      ) : fincasFiltradas.length === 0 ? (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          No hay fincas que coincidan con los filtros.
        </div>
      ) : (
        <div className="bg-white rounded shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold">Nombre</th>
                <th className="text-left px-4 py-3 text-sm font-semibold">Ubicación</th>
                <th className="text-left px-4 py-3 text-sm font-semibold">Estado</th>
                <th className="text-left px-4 py-3 text-sm font-semibold">Creada</th>
                <th className="text-right px-4 py-3 text-sm font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {fincasFiltradas.map((f) => (
                <tr
                  key={f.id}
                  className={`border-t ${!f.activo ? 'bg-gray-50 opacity-75' : 'hover:bg-gray-50'}`}
                >
                  <td className="px-4 py-3 font-medium">{f.nombre}</td>
                  <td className="px-4 py-3 text-gray-600">{f.ubicacion || '-'}</td>
                  <td className="px-4 py-3">
                    {f.activo ? (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">
                        ● Activa
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-semibold">
                        ● Inactiva
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {f.creado_en ? format(new Date(f.creado_en), 'dd/MM/yyyy') : '-'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {f.activo ? (
                      <>
                        <button
                          onClick={() => abrirAdminModal(f)}
                          className="text-purple-600 hover:underline mr-3 text-sm"
                        >
                          👤 Admin
                        </button>
                        <button
                          onClick={() => abrirEditar(f)}
                          className="text-blue-600 hover:underline mr-3 text-sm"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => desactivar(f)}
                          className="text-orange-600 hover:underline mr-3 text-sm"
                        >
                          Desactivar
                        </button>
                        <button
                          onClick={() => eliminarPermanente(f)}
                          className="text-red-600 hover:underline text-sm"
                          title="Eliminar permanentemente (irreversible)"
                        >
                          🗑️ Eliminar
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => abrirEditar(f)}
                          className="text-blue-600 hover:underline mr-3 text-sm"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => reactivar(f)}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 mr-3"
                        >
                          🔓 Reactivar
                        </button>
                        <button
                          onClick={() => eliminarPermanente(f)}
                          className="text-red-600 hover:underline text-sm"
                          title="Eliminar permanentemente (irreversible)"
                        >
                          🗑️ Eliminar
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal crear/editar finca */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editando ? 'Editar finca' : 'Nueva finca'}
            </h2>
            <form onSubmit={guardar}>
              <label className="block mb-3">
                <span className="text-sm font-medium">Nombre</span>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  autoFocus
                />
              </label>
              <label className="block mb-4">
                <span className="text-sm font-medium">Ubicación</span>
                <input
                  type="text"
                  value={form.ubicacion}
                  onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setMostrarModal(false)} className="px-4 py-2 border rounded hover:bg-gray-100">
                  Cancelar
                </button>
                <button type="submit" className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-800">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal crear admin */}
      {mostrarAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-1">Crear admin</h2>
            <p className="text-sm text-gray-500 mb-4">
              Finca: <strong>{mostrarAdminModal.nombre}</strong>
            </p>
            <form onSubmit={guardarAdmin}>
              <label className="block mb-3">
                <span className="text-sm font-medium">Nombre completo</span>
                <input
                  type="text"
                  value={adminForm.nombre}
                  onChange={(e) => handleAdminNombreChange(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  autoFocus
                />
              </label>
              <label className="block mb-3">
                <span className="text-sm font-medium">Email</span>
                <input
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </label>
              <label className="block mb-3">
                <span className="text-sm font-medium">Usuario</span>
                <input
                  type="text"
                  value={adminForm.username}
                  readOnly
                  className="mt-1 w-full px-3 py-2 border rounded bg-gray-100 font-mono text-gray-700 cursor-not-allowed"
                />
              </label>
              <label className="block mb-3">
                <span className="text-sm font-medium">Contraseña</span>
                <input
                  type="text"
                  value={adminForm.password}
                  readOnly
                  className="mt-1 w-full px-3 py-2 border rounded bg-gray-100 font-mono text-gray-700 cursor-not-allowed"
                />
                <span className="text-xs text-gray-500">
                  🔒 Se genera automáticamente (3 letras + 3 números).
                </span>
              </label>

              {generado.username && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-xs font-semibold text-blue-900 uppercase mb-2">
                    👁️ Credenciales del admin
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Usuario:</span>
                      <strong className="font-mono text-blue-900">{generado.username}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Contraseña:</span>
                      <strong className="font-mono text-blue-900">{generado.password}</strong>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={regenerar}
                    className="mt-2 text-xs text-blue-700 hover:underline"
                  >
                    🔄 Regenerar credenciales
                  </button>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setMostrarAdminModal(null)} className="px-4 py-2 border rounded hover:bg-gray-100">
                  Cancelar
                </button>
                <button type="submit" className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-800">
                  Crear admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}